/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { PageHeader } from "@/components/customCopmonent/Text/PageHeader";
import { RevealOnScroll } from "@/components/SpecialProjectsPage.primitives";

import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ✅ IMPORT PARTICLE CARD
import { ParticleCard } from "@/components/customCopmonent/MagicBento"; // adjust path

if (typeof window !== "undefined") {
	gsap.registerPlugin(ScrollTrigger);
}

// ================= TYPES =================
type CardItem = {
	image: string;
	title: string;
	description: string;
	href?: string;
};

// ================= APPLICATION SECTION =================
export const IndustrySectionApplication: React.FC<{
	applications: CardItem[];
	title?: string;
	description?: string;
}> = ({ applications, title, description }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		const anim = gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 60 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
			},
		);

		const trigger = ScrollTrigger.create({
			trigger: ref.current,
			start: "top 85%",
			animation: anim,
		});

		return () => trigger.kill();
	}, []);

	return (
		<div
			ref={ref}
			className="mb-20">
			{/* ✅ BENTO CSS */}
			<style>
				{`
				.card-bento {
					--glow-x: 50%;
					--glow-y: 50%;
					--glow-intensity: 0;
					--glow-radius: 180px;
					--glow-color: 249,169,58;
					position: relative;
				}

				.card-bento::after {
					content: '';
					position: absolute;
					inset: 0;
					padding: 1px;
					border-radius: inherit;
					background: radial-gradient(
						var(--glow-radius) circle at var(--glow-x) var(--glow-y),
						rgba(var(--glow-color), calc(var(--glow-intensity) * 0.8)) 0%,
						rgba(var(--glow-color), calc(var(--glow-intensity) * 0.4)) 30%,
						transparent 60%
					);
					-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
					-webkit-mask-composite: xor;
					mask-composite: exclude;
					pointer-events: none;
				}

				.card-bento:hover {
					--glow-intensity: 1;
					box-shadow: 0 12px 40px rgba(0,0,0,0.25),
								0 0 25px rgba(249,169,58,0.25);
				}
				`}
			</style>

			{title && (
				<RevealOnScroll>
					<PageHeader
						title={title}
						description={description}
						align="center"
						className="py-14"
					/>
				</RevealOnScroll>
			)}

			{/* GRID */}
			<div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto px-6">
				{applications.map((item, i) => (
					<div
						key={i}
						className="w-full sm:w-[48%] lg:w-[23%] flex justify-center">
						<IndustriesCard
							item={item}
							index={i}
						/>
					</div>
				))}
			</div>
		</div>
	);
};

// ================= PRODUCT SECTION =================
export const IndustrySectionProduct: React.FC<{
	applications: CardItem[];
	title?: string;
	description?: string;
}> = ({ applications, title, description }) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) return;

		const anim = gsap.fromTo(
			ref.current,
			{ opacity: 0, y: 60 },
			{
				opacity: 1,
				y: 0,
				duration: 0.8,
				ease: "power3.out",
			},
		);

		const trigger = ScrollTrigger.create({
			trigger: ref.current,
			start: "top 85%",
			animation: anim,
		});

		return () => trigger.kill();
	}, []);

	return (
		<div
			ref={ref}
			className="mb-20">
			{title && (
				<RevealOnScroll>
					<PageHeader
						title={title}
						description={description}
						align="center"
						className="py-14"
					/>
				</RevealOnScroll>
			)}

			{/* GRID */}
			<div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto px-6">
				{applications.map((item, i) => (
					<div
						key={i}
						className="w-full sm:w-[48%] lg:w-[31%] flex justify-center">
						{item.href ?
							<Link
								to={item.href}
								className="w-full flex justify-center">
								<IndustriesProductCard
									item={item}
									index={i}
								/>
							</Link>
						:	<IndustriesProductCard
								item={item}
								index={i}
							/>
						}
					</div>
				))}
			</div>
		</div>
	);
};

// ================= FINAL CARD =================
const IndustriesCard: React.FC<{
	item: CardItem;
	index?: number;
}> = ({ item, index = 0 }) => {
	const { t } = useTranslation();
	const [isHovered, setIsHovered] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [needsTruncation, setNeedsTruncation] = useState(false);
	const descriptionRef = useRef<HTMLParagraphElement>(null);

	useEffect(() => {
		if (descriptionRef.current) {
			const lineHeight = parseInt(
				getComputedStyle(descriptionRef.current).lineHeight,
			);
			const maxHeight = lineHeight * 4; // 4 lines max
			setNeedsTruncation(descriptionRef.current.scrollHeight > maxHeight);
		}
	}, [item.description]);

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ delay: index * 0.08, duration: 0.5 }}
			className="w-full max-w-sm cursor-target"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}>
			<ParticleCard
				className="card-bento rounded-2xl overflow-hidden group"
				particleCount={20}
				glowColor="249,169,58"
				enableTilt={false}
				enableMagnetism={false}>
				<div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl overflow-hidden transition-all duration-500 flex flex-col border border-gray-200 shadow-lg h-90">
					{/* IMAGE - Hover effects ONLY on product card images */}
					<div className="relative h-60 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
						<img
							src={item.image || "/noimg.png"}
							onError={(e) =>
								((e.currentTarget as HTMLImageElement).src = "/noimg.png")
							}
							className="w-full h-full object-cover transition-all duration-700 will-change-transform"
							style={{
								transform: isHovered ? "scale(1.1)" : "scale(1)",
							}}
							alt={item.title}
						/>
					</div>
					{/* TITLE */}
					<div className="h-15 flex items-center justify-center px-4 bg-gradient-to-r from-white to-gray-50 border-b border-gray-100">
						<h3 className="text-lg md:text-xl font-bold text-gray-800 text-center tracking-tight">
							{item.title}
						</h3>
					</div>

					<div className="flex-1 px-5 py-2 flex flex-col">
						<div
							className="text-sm leading-relaxed text-gray-600 text-center relative"
							style={{
								maxHeight: isExpanded ? "120px" : "2.6rem",
								overflowY: isExpanded ? "auto" : "hidden",
							}}>
							<p
								ref={descriptionRef}
								className="inline">
								{item.description}

								{/* INLINE SEE MORE */}
								{needsTruncation && !isExpanded && (
									<button
										type="button"
										onClick={toggleExpand}
										aria-expanded={isExpanded}
										className="ml-1 inline cursor-pointer bg-transparent p-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F9A93A]"
										style={{ color: "#F9A93A" }}>
										...{t("common.seeMore")}
									</button>
								)}

								{/* INLINE SEE LESS */}
								{isExpanded && (
									<button
										type="button"
										onClick={toggleExpand}
										aria-expanded={isExpanded}
										className="ml-2 inline cursor-pointer bg-transparent p-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F9A93A]"
										style={{ color: "#F9A93A" }}>
										{t("common.seeLess")}
									</button>
								)}
							</p>
						</div>
					</div>
				</div>
			</ParticleCard>
		</motion.div>
	);
};

// ================= FINAL CARD =================
const IndustriesProductCard: React.FC<{
	item: CardItem;
	index?: number;
}> = ({ item, index = 0 }) => {
	const { t } = useTranslation();
	const [isHovered, setIsHovered] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [needsTruncation, setNeedsTruncation] = useState(false);
	const descriptionRef = useRef<HTMLParagraphElement>(null);

	useEffect(() => {
		if (descriptionRef.current) {
			const lineHeight = parseInt(
				getComputedStyle(descriptionRef.current).lineHeight,
			);
			const maxHeight = lineHeight * 4; // 4 lines max
			setNeedsTruncation(descriptionRef.current.scrollHeight > maxHeight);
		}
	}, [item.description]);

	const toggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true }}
			transition={{ delay: index * 0.08, duration: 0.5 }}
			className="w-full max-w-sm cursor-target"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}>
			<ParticleCard
				className="card-bento rounded-2xl overflow-hidden group"
				particleCount={20}
				glowColor="249,169,58"
				enableTilt={false}
				enableMagnetism={false}>
				<div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl overflow-hidden transition-all duration-500 flex flex-col border border-gray-200 shadow-lg h-90	">
					{/* IMAGE - Hover effects ONLY on product card images */}
					<div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
						<img
							src={item.image || "/noimg.png"}
							onError={(e) =>
								((e.currentTarget as HTMLImageElement).src = "/noimg.png")
							}
							className="w-full h-full object-cover transition-all duration-700 will-change-transform"
							style={{
								transform: isHovered ? "scale(1.1)" : "scale(1)",
							}}
							alt={item.title}
						/>
						<div
							className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 transition-opacity duration-500"
							style={{ opacity: isHovered ? 1 : 0 }}
						/>

						{/* Icon overlay on hover */}
						<motion.div
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{
								opacity: isHovered ? 1 : 0,
								scale: isHovered ? 1 : 0.8,
							}}
							className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
							<svg
								className="w-12 h-12 text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M13 7l5 5m0 0l-5 5m5-5H6"
								/>
							</svg>
						</motion.div>
					</div>

					{/* TITLE */}
					<div className="h-15 flex items-center justify-center px-4 bg-gradient-to-r from-white to-gray-50 border-b border-gray-100">
						<h3 className="text-lg md:text-xl font-bold text-gray-800 text-center tracking-tight">
							{item.title}
						</h3>
					</div>

					{/* DESCRIPTION with expand/collapse */}
					<div className="flex-1 px-5 py-2 flex flex-col">
						<div
							className="text-sm leading-relaxed text-gray-600 text-center relative"
							style={{
								maxHeight: isExpanded ? "120px" : "2.6rem",
								overflowY: isExpanded ? "auto" : "hidden",
							}}>
							<p
								ref={descriptionRef}
								className="inline">
								{item.description}

								{/* INLINE SEE MORE */}
								{needsTruncation && !isExpanded && (
									<button
										type="button"
										onClick={toggleExpand}
										aria-expanded={isExpanded}
										className="ml-1 inline cursor-pointer bg-transparent p-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F9A93A]"
										style={{ color: "#F9A93A" }}>
										...{t("common.seeMore")}
									</button>
								)}

								{/* INLINE SEE LESS */}
								{isExpanded && (
									<button
										type="button"
										onClick={toggleExpand}
										aria-expanded={isExpanded}
										className="ml-2 inline cursor-pointer bg-transparent p-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F9A93A]"
										style={{ color: "#F9A93A" }}>
										{t("common.seeLess")}
									</button>
								)}
							</p>
						</div>
					</div>
				</div>
			</ParticleCard>
		</motion.div>
	);
};
